import * as React from 'react'

import viewCountService from '@/services/view-count.service'

import {
  BulkViewStatsResponse,
  HealthCheckResponse,
  QueueStatsResponse,
  TrackViewRequest,
  ViewCountEntity,
  ViewCountEntityType,
  ViewStatsResponse,
} from '@/types/view-count.type'

interface UseViewCountReturn {
  // Track view
  trackView: (options: TrackViewRequest) => Promise<void>
  isTracking: boolean

  // Get stats
  getViewStats: (
    entityType: ViewCountEntityType,
    entityId: string,
    windowHours?: number
  ) => Promise<ViewStatsResponse | null>
  getBulkViewStats: (entities: ViewCountEntity[], windowHours?: number) => Promise<BulkViewStatsResponse | null>
  isGettingStats: boolean

  // Health check
  healthCheck: () => Promise<HealthCheckResponse | null>
  isHealthChecking: boolean

  // Queue management
  getQueueStats: () => Promise<QueueStatsResponse | null>
  clearQueue: () => Promise<boolean>
  isManagingQueue: boolean

  // Sync
  triggerSync: (entityType: ViewCountEntityType, entityId: string) => Promise<boolean>
  isTriggeringSync: boolean

  // Error handling
  error: string | null
  clearError: () => void
}

export const useViewCount = (): UseViewCountReturn => {
  const [isTracking, setIsTracking] = React.useState(false)
  const [isGettingStats, setIsGettingStats] = React.useState(false)
  const [isHealthChecking, setIsHealthChecking] = React.useState(false)
  const [isManagingQueue, setIsManagingQueue] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [isTriggeringSync, setIsTriggeringSync] = React.useState(false)

  const clearError = React.useCallback(() => {
    setError(null)
  }, [])

  const triggerSync = React.useCallback(async (entityType: ViewCountEntityType, entityId: string): Promise<boolean> => {
    setIsTriggeringSync(true)
    setError(null)

    try {
      await viewCountService.triggerSync(entityType, entityId)
      return true
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to trigger sync:', err)
      setError(err instanceof Error ? err.message : 'Failed to trigger sync')
      return false
    } finally {
      setIsTriggeringSync(false)
    }
  }, [])

  const trackView = React.useCallback(async (options: TrackViewRequest) => {
    setIsTracking(true)
    setError(null)

    try {
      await viewCountService.trackView(options)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to track view')
      throw err
    } finally {
      setIsTracking(false)
    }
  }, [])

  const getViewStats = React.useCallback(
    async (
      entityType: ViewCountEntityType,
      entityId: string,
      windowHours?: number
    ): Promise<ViewStatsResponse | null> => {
      setIsGettingStats(true)
      setError(null)

      try {
        const response = await viewCountService.getViewStats(entityType, entityId, windowHours)
        return response.data
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to get view statistics')
        return null
      } finally {
        setIsGettingStats(false)
      }
    },
    []
  )

  const getBulkViewStats = React.useCallback(
    async (entities: ViewCountEntity[], windowHours?: number): Promise<BulkViewStatsResponse | null> => {
      setIsGettingStats(true)
      setError(null)

      try {
        const response = await viewCountService.getBulkViewStats(entities, windowHours)
        return response.data
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to get bulk view statistics')
        return null
      } finally {
        setIsGettingStats(false)
      }
    },
    []
  )

  const healthCheck = React.useCallback(async (): Promise<HealthCheckResponse | null> => {
    setIsHealthChecking(true)
    setError(null)

    try {
      const response = await viewCountService.healthCheck()
      return response.data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to perform health check')
      return null
    } finally {
      setIsHealthChecking(false)
    }
  }, [])

  const getQueueStats = React.useCallback(async (): Promise<QueueStatsResponse | null> => {
    setIsManagingQueue(true)
    setError(null)

    try {
      const response = await viewCountService.getQueueStats()
      return response.data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get queue statistics')
      return null
    } finally {
      setIsManagingQueue(false)
    }
  }, [])

  const clearQueue = React.useCallback(async (): Promise<boolean> => {
    setIsManagingQueue(true)
    setError(null)

    try {
      const response = await viewCountService.clearQueue()
      return response.data.success
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear queue')
      return false
    } finally {
      setIsManagingQueue(false)
    }
  }, [])

  return {
    trackView,
    isTracking,
    getViewStats,
    getBulkViewStats,
    isGettingStats,
    healthCheck,
    isHealthChecking,
    getQueueStats,
    clearQueue,
    isManagingQueue,
    error,
    clearError,
    triggerSync,
    isTriggeringSync,
  }
}
